import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { announcementApi } from '@/api/announcementApi'
import type { Announcement } from '@/features/announcements/types'
import type { SnowflakeId } from '@/types/snowflake'

interface AnnouncementsState {
  items: Announcement[]
  /**
   * 正在查看详情的公告 id。抬到全局是因为详情入口现在在通知收件箱的
   * Announcements 页签里(R-044) —— 那个列表活在 Popover 内部，弹 Dialog 会
   * 让 Popover 因焦点移出而关闭，详情跟着一起卸载。id 放 store、Dialog 由
   * 全局的 <AnnouncementsHost/> 渲染，收件箱关掉也不影响详情。
   */
  detailId: SnowflakeId | null
  loading: boolean
  loaded: boolean
  error: string | null
}

const initialState: AnnouncementsState = { items: [], detailId: null, loading: false, loaded: false, error: null }

/** Tier 0 truth source: the pending list (published + in-validity + targeted + unread + un-snoozed). */
export const fetchPendingAnnouncements = createAsyncThunk('announcements/fetchPending', async () => {
  const res = await announcementApi.pending()
  return Array.isArray(res) ? res : []
})

const announcementsSlice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    setAnnouncements: (state, action: PayloadAction<Announcement[]>) => {
      state.items = action.payload
    },
    removeAnnouncement: (state, action: PayloadAction<SnowflakeId>) => {
      state.items = state.items.filter((x) => x.id !== action.payload)
    },
    openAnnouncementDetail: (state, action: PayloadAction<SnowflakeId>) => {
      state.detailId = action.payload
    },
    closeAnnouncementDetail: (state) => {
      state.detailId = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingAnnouncements.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPendingAnnouncements.fulfilled, (state, action) => {
        state.loading = false
        state.loaded = true
        state.items = action.payload
      })
      .addCase(fetchPendingAnnouncements.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to load announcements'
      })
  },
})

export const {
  setAnnouncements, removeAnnouncement, openAnnouncementDetail, closeAnnouncementDetail,
} = announcementsSlice.actions
export default announcementsSlice.reducer
